/**
 * Where the month's money went — the part of the dashboard that is this product and not a
 * spending tracker (spec v2 22.2).
 *
 * Every figure comes from `app.money_flow`; the rows below only decide what to show and what
 * to leave out. A bucket at zero is hidden, because "Trả nợ gốc 0 ₫" is noise on a month with
 * no debt — except the three that are always meaningful: what came in, what went out, and
 * what is left.
 */
import { useId } from 'react';

import type { Allocation, MoneyIn } from '@pfo/analytics';

import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import lists from '../../../shared/styles/lists.module.css';
import { ALLOCATION_LABELS, STABILITY_LABELS } from '../model/homeLabels.js';
import styles from './AllocationCard.module.css';

export function AllocationCard({
  moneyIn,
  allocation,
}: {
  moneyIn: MoneyIn;
  allocation: Allocation;
}) {
  const headingId = useId();

  const rows: ReadonlyArray<{ label: string; amount: number; alwaysShow?: boolean }> = [
    { label: ALLOCATION_LABELS.expense, amount: allocation.expense, alwaysShow: true },
    { label: ALLOCATION_LABELS.saving, amount: allocation.saving },
    { label: ALLOCATION_LABELS.reserve, amount: allocation.reserve },
    { label: ALLOCATION_LABELS.investment, amount: allocation.investment },
    { label: ALLOCATION_LABELS.debtPrincipal, amount: allocation.debtPrincipal },
    { label: ALLOCATION_LABELS.paidForFamily, amount: allocation.paidForFamily },
  ];

  const visible = rows.filter((row) => row.alwaysShow === true || row.amount !== 0);

  return (
    <section className={lists.card} aria-labelledby={headingId}>
      <h2 className={lists.cardTitle} id={headingId}>
        Tiền đi đâu
      </h2>

      <ul className={lists.list}>
        <li>
          <div className={lists.row}>
            <span className={lists.rowText}>
              <span className={lists.rowName}>Tiền vào</span>
              <span className={lists.rowMeta}>
                {STABILITY_LABELS['stable']} {formatVnd(moneyIn.stableTotal)}
                {moneyIn.variableTotal !== 0 ? ` · khác ${formatVnd(moneyIn.variableTotal)}` : ''}
              </span>
            </span>
            <span className={cx(lists.amount, styles.amountIn)}>{formatVnd(moneyIn.total)}</span>
          </div>
        </li>

        {/* Money from a spouse sits beside income rather than inside it (A2, step 1.8): it is
            not something this person earned, but it is cash they now hold, so "Còn lại"
            below counts it. Always 0 on the family report, where it would be double
            counting. */}
        {moneyIn.receivedFromFamily !== 0 ? (
          <li>
            <div className={lists.row}>
              <span className={lists.rowText}>
                <span className={lists.rowName}>{ALLOCATION_LABELS.receivedFromFamily}</span>
              </span>
              <span className={cx(lists.amount, styles.amountIn)}>
                {formatVnd(moneyIn.receivedFromFamily)}
              </span>
            </div>
          </li>
        ) : null}

        {visible.map((row) => (
          <li key={row.label}>
            <div className={lists.row}>
              <span className={lists.rowText}>
                <span className={lists.rowName}>{row.label}</span>
              </span>
              <span className={lists.amount}>{formatVnd(row.amount)}</span>
            </div>
          </li>
        ))}

        <li>
          <div className={lists.row}>
            <span className={lists.rowText}>
              <span className={lists.rowName}>{ALLOCATION_LABELS.freeCash}</span>
              {/* Decision B10: the number the owner has planned with for years is measured
                  against dependable income, so it is shown next to the other one rather than
                  replaced by it. */}
              <span className={lists.rowMeta}>
                Trên thu nhập ổn định: {formatVnd(allocation.freeCashStable)}
              </span>
              {/* AJ1: an opening balance is a state, not income (DBD-006), so a month with no
                  recorded pay reads as "spent with nothing coming in". The figure stays; the
                  line says why, and where the income is usually recorded. */}
              {moneyIn.stableTotal === 0 && allocation.freeCashStable < 0 ? (
                <span className={lists.rowMeta}>
                  Tháng này chưa ghi khoản thu ổn định nào nên dòng tiền âm — xác nhận Lương ở Đến
                  hạn hoặc thêm khoản thu.
                </span>
              ) : null}
            </span>
            <span
              className={cx(
                lists.amount,
                allocation.freeCash < 0 ? lists.amountDebt : styles.amountIn,
              )}
            >
              {formatVnd(allocation.freeCash)}
            </span>
          </div>
        </li>
      </ul>
    </section>
  );
}
